/**
 * ControlsTable — DataTable + TableToolbar wrapper for the Controls
 * half of Step 8. Search-only (control counts are small; no obvious
 * filter dimension since `control_type` is free-text).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  CONTROL_COLUMNS_READ,
  CONTROL_COLUMNS_EDIT,
  CONTROL_ACTIONS_COLUMN_WIDTH,
  type ControlRecord,
} from './controlsFieldSchema';
import styles from './Step8Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState { column: string; direction: 'asc' | 'desc'; }

export interface ControlsTableProps {
  controls: ControlRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (control: ControlRecord) => void;
  onDelete?: (control: ControlRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
}

function sortRows(rows: ControlRecord[], sort: SortState | null): ControlRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchRows(rows: ControlRecord[], query: string): ControlRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function ControlsTable({
  controls,
  loading,
  emptyMessage = 'No cybersecurity controls yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: ControlsTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [typeValues, setTypeValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // control_type is free-text — derive options + counts from the live row
  // set so the Type section reflects whatever the agent produced.
  const typeOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of controls) {
      const k = String(c.control_type ?? '').trim();
      if (k) counts[k] = (counts[k] ?? 0) + 1;
    }
    return Object.keys(counts).sort().map((v) => ({ value: v, label: v, count: counts[v] }));
  }, [controls]);

  const typeFiltered = useMemo(
    () => (typeValues.length === 0
      ? controls
      : controls.filter((c) => typeValues.includes(String(c.control_type ?? '')))),
    [controls, typeValues],
  );
  const searched = useMemo(() => searchRows(typeFiltered, search), [typeFiltered, search]);
  const sorted = useMemo(() => sortRows(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () => paged.map((r, i) => ({ ...r, _rowKey: `${r.control_id ?? 'row'}::${(page - 1) * pageSize + i}` })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return CONTROL_COLUMNS_READ;
    return [
      ...CONTROL_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: CONTROL_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: ControlRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.control_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const typeFilter = {
    key: 'control_type',
    label: 'Type',
    values: typeValues,
    onChange: (next: string[]) => { setTypeValues(next); setPage(1); },
    options: typeOptions,
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Control
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny isOpen={filterOpen} onClose={() => setFilterOpen(false)} filters={[typeFilter]} />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search controls…',
            }}
            count={{ value: sorted.length, label: 'Controls' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || typeValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => { setPageSize(size); setPage(1); }}
          />
        }
      />
    </div>
  );
}
